import { Alert } from '@designsforhealth/dfh-react-components'
import { CircularProgress, Grid, Typography } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import React, { useCallback, useContext, useEffect, useState } from 'react'

import useSiteMetadata from '../../../hooks/useSiteMetadata'
import CheckoutLayout from '../../../layouts/buy/CheckoutLayout'
import { NonEmptyCartContext } from '../../../lib/cart'
import useCheckoutNavBarOptionValue from '../../../lib/cart/hooks/useCheckoutNavBarOptionValue'
import { OrderType } from '../../../lib/cart/item'
import { refetchCustomerCartWithInterval$ } from '../../../lib/cart/state/cart'
import {
  cartItemCheckSetFromFeatureFlags,
  useAutoRemoveErrantCartItems,
} from '../../../lib/cart/state/cart-items/cart-item-remove'
import { formatAsCurrencyOrZero } from '../../../utils/priceTools'
import { CartItemErrors } from '../../cart/CartItemErrors'
import ContactSupportMessage from '../../ContactSupportMessage'
import Spinner from '../../Spinner'
import { GridButton } from '../shared/GridButton'
import AddressSummary from '../summary/AddressSummary'
import AutoShipTerms from '../summary/AutoShipTerms'
import CaliforniaProp65Warning from '../summary/CaliforniaProp65Warning'
import CreditCardPaymentMethodSummary from '../summary/CreditCardPaymentMethodSummary'
import DeliveryMethodSummary from '../summary/DeliveryMethodSummary'
import MobileCartItemsSummary from '../summary/MobileCartItemsSummary'
import PurchaseOrderPaymentMethodSummary from '../summary/PurchaseOrderPaymentMethodSummary'
import TotalSummary from '../summary/TotalSummary'
import useStyles from './styles/review'

const appendPeriodIfMissing = (value: string) =>
  !value || /\. *$/.test(value) ? value : `${value}.`

const enum AutoShipConfirmStatus {
  NOT_APPLICABLE,
  PENDING,
  CONFIRMED,
}

export interface ReviewProps {
  networkError: boolean
  onSubmit: () => void
  otherError: boolean
  outOfStockError: string
  paymentError: string
  quantityError: string
  submitting: boolean
}

const ReviewReactive: React.FC<ReviewProps> = ({
  networkError,
  onSubmit,
  outOfStockError,
  otherError,
  paymentError,
  quantityError,
  submitting,
}) => {
  const classes = useStyles()
  const customerCart = useContext(NonEmptyCartContext)
  const { featureFlags } = useSiteMetadata() ?? {}
  const { loading: cartItemErrorsLoading } = useAutoRemoveErrantCartItems(
    cartItemCheckSetFromFeatureFlags(featureFlags),
  )

  const displayErrorRef: React.RefCallback<HTMLDivElement> = useCallback((errorRef) => {
    if (errorRef) {
      errorRef.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    // refetch cart every minute
    const subscription = refetchCustomerCartWithInterval$(60000).subscribe()
    return () => subscription.unsubscribe()
  }, [])

  const {
    billing_address: billingAddress,
    items,
    prices,
    shipping_addresses: [shippingAddress],
  } = customerCart
  const shippingMethod = shippingAddress?.selected_shipping_method
  const { navBarOptionValue, loading: loadingNavBarOptions } =
    useCheckoutNavBarOptionValue('review')

  const [autoShipConfirmStatus, setAutoShipConfirmStatus] = useState(() =>
    featureFlags?.autoShipConfirmation &&
    items?.find((item) => item?.item_attributes?.dfh_order_type === OrderType.AutoShip) != null
      ? AutoShipConfirmStatus.PENDING
      : AutoShipConfirmStatus.NOT_APPLICABLE,
  )

  if (loadingNavBarOptions) return <Spinner loading />

  const displayError =
    !submitting && !!(networkError || otherError || outOfStockError || quantityError)

  return (
    <CheckoutLayout className={classes.review} checkoutNavigationBarTabValue={navBarOptionValue}>
      <Grid className={classes.reviewHeading} item xs={12}>
        <Typography variant="h5">REVIEW ORDER</Typography>
      </Grid>
      <Grid justifyContent="center" container item xs={12}>
        <CartItemErrors />
      </Grid>
      {displayError && (
        <Grid ref={displayErrorRef} item xs={12}>
          {networkError && (
            <Alert type="error">
              A network error occurred, and we have been notified. <ContactSupportMessage />
            </Alert>
          )}
          {otherError && (
            <Alert type="error">
              An error occurred placing your order, and we have been notified.{' '}
              <ContactSupportMessage />
            </Alert>
          )}
          {outOfStockError && (
            <Alert type="error">
              {appendPeriodIfMissing(outOfStockError)} <ContactSupportMessage />
            </Alert>
          )}
          {quantityError && (
            <Alert type="error">
              {appendPeriodIfMissing(quantityError)} <ContactSupportMessage />
            </Alert>
          )}
        </Grid>
      )}
      {shippingMethod && (
        <DeliveryMethodSummary
          showEdit
          description={`${shippingMethod.carrier_title} ${shippingMethod.method_title}`}
          shippingAmount={shippingMethod.amount?.value || 0}
          holdUntilMonday={customerCart.hold_until_monday || ''}
        />
      )}
      {shippingAddress && (
        <AddressSummary
          showEdit
          address={shippingAddress}
          title="Shipping Address"
          navigateTo="shipping/"
        />
      )}
      {billingAddress && (
        <AddressSummary
          showEdit
          address={billingAddress}
          title="Billing Address"
          navigateTo="payment/"
        />
      )}
      <CreditCardPaymentMethodSummary
        showEdit
        selectedPaymentMethod={customerCart.selected_payment_method}
        error={paymentError}
      />
      <PurchaseOrderPaymentMethodSummary
        showEdit
        selectedPaymentMethod={customerCart.selected_payment_method}
        error={paymentError}
      />
      <CaliforniaProp65Warning
        className={classes.mobileProp65Warning}
        items={customerCart.items}
        shippingAddress={shippingAddress}
      />
      <MobileCartItemsSummary cart={customerCart} suppressHeader />
      <TotalSummary
        cart={customerCart}
        roundBottomCorners={autoShipConfirmStatus === AutoShipConfirmStatus.NOT_APPLICABLE}
      />
      {autoShipConfirmStatus !== AutoShipConfirmStatus.NOT_APPLICABLE && (
        <AutoShipTerms
          confirmed={autoShipConfirmStatus === AutoShipConfirmStatus.CONFIRMED}
          onConfirm={() => setAutoShipConfirmStatus(AutoShipConfirmStatus.CONFIRMED)}
        />
      )}
      <Grid
        className={classes.reviewFooter}
        container
        item
        xs={12}
        alignItems="center"
        justifyContent="center"
      >
        <GridButton
          disabled={
            submitting ||
            !!paymentError ||
            cartItemErrorsLoading ||
            autoShipConfirmStatus === AutoShipConfirmStatus.PENDING
          }
          onClick={onSubmit}
          type="submit"
          id="place-order-button"
          data-cy="place-order"
        >
          {submitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            `PLACE ORDER: ${formatAsCurrencyOrZero({ amount: prices?.grand_total?.value || 0 })}`
          )}
        </GridButton>
      </Grid>
    </CheckoutLayout>
  )
}

const Review: React.FC<ReviewProps> = (props) => (
  <Subscribe>
    <ReviewReactive {...props} />
  </Subscribe>
)

export default Review
